// Programmatic Botpress conversation runner.
//
// Drives a full conversation against a bot using only the Chat API primitives
// (createUser -> createConversation -> sendMessage -> listConversationMessages),
// with no visual webchat widget. Given an ordered list of user messages it sends
// each one, waits for the bot's reply (or replies), and returns the assembled
// transcript along with the conversation id.

import * as BotpressAPI from "./omniBotpressAPI.js";

const sleep = ms => new Promise(resolve => setTimeout(resolve, ms));

// Pull the plain text out of a Chat API message payload, regardless of shape.
const extractText = message => {
  if (message == null) {
    return "";
  }
  if (typeof message.payload?.text === "string") {
    return message.payload.text;
  }
  if (typeof message.payload?.block?.text === "string") {
    return message.payload.block.text;
  }
  return "";
};

// Sort messages oldest -> newest so the transcript reads in order.
const byCreatedAtAsc = (a, b) => new Date(a.createdAt) - new Date(b.createdAt);

// Poll the conversation until the bot has produced more messages than we have
// already seen, or until the timeout elapses. Returns only the new bot replies.
const waitForBotReplies = async ({
  botODM,
  key,
  conversationId,
  userId,
  seenBotCount,
  timeoutMs,
  pollMs,
}) => {
  const deadline = Date.now() + timeoutMs;

  while (Date.now() < deadline) {
    await sleep(pollMs);

    const data = await BotpressAPI.listConversationMessages({ botODM, key, conversationId });
    const all = Array.isArray(data?.messages) ? data.messages : [];

    // Bot messages are everything not authored by the user we created.
    const botMessages = all.filter(message => message.userId !== userId).sort(byCreatedAtAsc);

    if (botMessages.length > seenBotCount) {
      return botMessages.slice(seenBotCount);
    }
  }

  return [];
};

/**
 * Run a scripted conversation programmatically.
 *
 * @param {Object}   params
 * @param {Object}   params.botODM         - The botpress bot ODM (provides webchatClientId).
 * @param {string[]} params.messages       - Ordered user messages to send.
 * @param {Function} [params.onProgress]   - Called with progress events as the run proceeds.
 * @param {number}   [params.replyTimeoutMs=20000] - Max time to wait for each bot reply.
 * @param {number}   [params.pollMs=1500]  - Poll interval while waiting for a reply.
 * @returns {Promise<{ conversationId: string, transcript: Array }>}
 */
export const runBotpressConversation = async ({
  botODM,
  messages,
  onProgress = () => {},
  replyTimeoutMs = 20000,
  pollMs = 1500,
}) => {
  if (botODM == null) {
    throw new Error("runBotpressConversation: botODM is required");
  }
  const cleanedMessages = (messages || []).map(m => `${m}`.trim()).filter(m => m.length > 0);
  if (cleanedMessages.length === 0) {
    throw new Error("runBotpressConversation: at least one message is required");
  }

  onProgress({ phase: "creating-user", total: cleanedMessages.length });
  const userResponse = await BotpressAPI.createUser({ botODM });
  const key = userResponse?.key;
  const userId = userResponse?.user?.id;
  if (!key) {
    throw new Error("runBotpressConversation: failed to create user (no key returned)");
  }

  onProgress({ phase: "creating-conversation", total: cleanedMessages.length });
  const conversationResponse = await BotpressAPI.createConversation({ botODM, key });
  const conversationId = conversationResponse?.conversation?.id;
  if (!conversationId) {
    throw new Error("runBotpressConversation: failed to create conversation (no id returned)");
  }

  const transcript = [];
  let seenBotCount = 0;

  for (let index = 0; index < cleanedMessages.length; index++) {
    const text = cleanedMessages[index];

    onProgress({ phase: "sending", index, total: cleanedMessages.length, text });
    transcript.push({ direction: "outgoing", text });
    await BotpressAPI.sendMessage({ botODM, key, conversationId, text });

    onProgress({ phase: "awaiting-reply", index, total: cleanedMessages.length, text });
    const replies = await waitForBotReplies({
      botODM,
      key,
      conversationId,
      userId,
      seenBotCount,
      timeoutMs: replyTimeoutMs,
      pollMs,
    });

    seenBotCount += replies.length;
    replies.forEach(reply => {
      transcript.push({ direction: "incoming", text: extractText(reply) });
    });

    onProgress({
      phase: "reply",
      index,
      total: cleanedMessages.length,
      replies: replies.map(extractText),
    });
  }

  onProgress({ phase: "done", total: cleanedMessages.length, conversationId });
  return { conversationId, transcript };
};

/**
 * Load a previously-saved conversation and return its user questions in the
 * order they were originally asked.
 *
 * @param {Object} params
 * @param {Object} params.botODM
 * @param {string} params.conversationId
 * @returns {Promise<string[]>}
 */
export const loadConversationQuestions = async ({ botODM, conversationId }) => {
  const returnedMessages = await BotpressAPI.loadConversation({ botODM, conversationId });
  // loadConversation returns newest-first; the user's turns are "incoming".
  return (returnedMessages || [])
    .reverse()
    .filter(message => message.direction === "incoming")
    .map(message => message.payload?.text || "")
    .filter(text => text.trim().length > 0);
};

/**
 * Rerun a previously-saved conversation: load its user questions and replay
 * them against the bot via the Chat API (no webchat). Building block for both
 * the single "Quick Rerun" and the "Run all flagged" batch flow.
 *
 * @param {Object}   params
 * @param {Object}   params.botODM                - Bot to replay the conversation against.
 * @param {Object}   [params.sourceBotODM]        - Bot to read the original questions from
 *                                                  (defaults to botODM). Lets a conversation
 *                                                  saved in one bot (e.g. PROD) be replayed
 *                                                  against another (e.g. the TEST bot).
 * @param {string}   params.conversationId        - The saved conversation to replay.
 * @param {Function} [params.onProgress]          - Forwarded to runBotpressConversation.
 * @param {number}   [params.replyTimeoutMs]
 * @param {number}   [params.pollMs]
 * @returns {Promise<{ sourceConversationId, conversationId, transcript, questions }>}
 */
export const rerunSavedConversation = async ({
  botODM,
  sourceBotODM,
  conversationId,
  onProgress = () => {},
  replyTimeoutMs,
  pollMs,
}) => {
  const questions = await loadConversationQuestions({
    botODM: sourceBotODM || botODM,
    conversationId,
  });
  if (questions.length === 0) {
    throw new Error("rerunSavedConversation: conversation has no user questions to replay");
  }

  const result = await runBotpressConversation({
    botODM,
    messages: questions,
    onProgress,
    replyTimeoutMs,
    pollMs,
  });

  return {
    sourceConversationId: conversationId,
    conversationId: result.conversationId,
    transcript: result.transcript,
    questions,
  };
};

export default runBotpressConversation;
